/**
 * Utility to map procedure names to their route IDs for standard procedures
 * This ensures consistent routing across the application
 */

import { getSubstantivePrefixFromWorkspace, WORKSPACE_TO_SUBSTANTIVE_PREFIX } from "./substantive-procedures-mapping";

/**
 * Workspaces that include "substantive" in their procedure routes
 * All other workspaces use the format: <base-prefix>-<procedure-id> (without "substantive")
 * 
 * Examples:
 * - WITH: "payroll-substantive-ipe_testing", "comc-substantive-ipe-testing"
 * - WITHOUT: "ppe-ipe-testing", "ia-ipe-testing", "ll-lease-assessment"
 */
const WORKSPACES_WITH_SUBSTANTIVE_IN_ROUTE: Set<string> = new Set([
  "Employee Benefits Expense", // payroll-substantive-ipe_testing
  "Cost of Materials Consumed", // comc-substantive-ipe-testing
  "Cash & Cash Equivalents", // cce-substantive-bank-accounts
  "Investments", // invsmt-substantive-investments-procedures
  "Provisions & Contingent Liabilities", // pcl-substantive-other-provisions
  "Other Expenses", // othexp-substantive-ipe-testing
  "Opening Balance Testing", // opnbal-substantive-inquiry-predecessor
  "Borrowings", // bor-substantive-repayment-testing
  "Borrowings - Ind AS", // borindas-substantive-*
  "Revenue from Operations", // rfo-substantive-*
]);

/**
 * Maps procedure names to their route IDs for each workspace
 * This matches the mapping used in left-panel.tsx
 */
const PROCEDURE_NAME_TO_ID: Record<string, Record<string, string>> = {
  "Employee Benefits Expense": {
    "IPE Testing": "ipe_testing",
    "Exception Testing": "exception_testing",
    "Headcount Reconciliation": "headcount_reconciliation",
    "M-o-M Analysis": "mom_analysis",
    "Increment Analysis": "increment_analysis",
    "Substantive Analytical Procedures": "salary_analytical",
    "Actuary Testing": "actuary_testing",
    "Managerial Remuneration": "managerial_remuneration",
    "Share Based Payment": "share_based_payment",
  },
  "Property, Plant & Equipment": {
    "IPE Testing": "ipe-testing",
    "Exception Testing": "exception-testing",
    "Additions & Deletions Analysis": "additions-deletions",
    "CWIP Analysis": "cwip-analysis",
    "Non-Current Assets Held for Sale": "non-current-assets-held-for-sale",
  },
  "Intangible Assets": {
    "IPE Testing": "ipe-testing",
    "Exception Testing": "exception-testing",
    "Additions & Deletions Listing": "additions-deletions",
    "Intangible Assets Under Development": "under-development",
  },
  "Lease Liability": {
    "Lease Assessment": "lease-assessment",
    "Finance Lease": "finance-lease",
    "Operating Lease": "operating-lease",
  },
  "Lease Liability - Ind AS": {
    "Lease Assessment": "lease-assessment",
    "Lease Testing": "lease-testing",
    "Lease Reassessment": "lease-reassessment",
    "Lease Modification": "lease-modification",
    "Lease Accounting Exception": "lease-accounting-exception",
    "Lease Disclosure": "lease-disclosure",
  },
  "Right-of-Use Assets": {
    "ROU Disclosure": "disclosure",
  },
  "Cost of Materials Consumed": {
    "IPE Testing": "ipe-testing",
    "Exception Testing": "exception-testing",
    "Test of Details": "test-of-details",
    "Analytical Procedures": "analytical-procedures",
    "Predictive Analytics": "predictive-analytics",
    "Purchase Analytical Dashboard": "purchase-analytical-dashboard",
  },
  "Trade Payables": {
    "IPE Testing": "ipe-testing",
    "Exception Testing": "exception-testing",
    "Ratio Analysis": "ratio-analysis",
    "Confirmations": "confirmations",
  },
  "Inventory": {
    "IPE Testing": "ipe-testing",
    "Exception Testing": "exception-testing",
    "NRV Assessment": "nrv-assessment",
    "Cost Allocation": "cost-allocation",
    "Inventory Ageing": "inventory-ageing",
    "Physical Verification": "physical-verification",
    "Overhead Working": "overhead-working",
  },
  "Other Assets": {
    "Prepaid Expense": "prepaid",
    "Security Deposit": "sec-deposit",
    "Advance to Suppliers - Completeness Check": "adv-suppliers-completeness",
    "Capital Advances - Completeness Check": "capital-advances-completeness",
  },
  "Other Financial Assets": {
    "Security Deposits": "security-deposits",
  },
  "Trade Receivables": {
    "IPE Testing": "ipe-testing",
    "Exception Testing": "exception-testing",
    "Ratio Analysis": "ratio-analysis",
    "ECL Flow Rate Analysis – YoY": "ecl-flow-rate-yoy",
    "ECL Flow Rate Analysis – QoQ": "ecl-flow-rate-qoq",
  },
  "Depreciation": {
    "Depreciation on ROU": "depreciation-rou",
    "Depreciation on Tangible Assets": "depreciation-tangible",
    "Depreciation on Intangible Assets": "depreciation-intangible",
  },
  "Other Expenses": {
    "IPE Testing": "ipe-testing",
    "Analytical Procedures": "analytical-procedures",
    "Corporate Social Responsibility": "corporate-social-responsibility",
  },
  "Borrowings": {
    "Repayment testing": "repayment-testing",
    "Covenant testing": "covenant-testing",
    "Drawing Power": "drawing-power",
    "Agreement Summary": "agreement-summary",
    "Quarterly Stock Statement": "quarterly-stock-statement",
    "Classification testing": "classification-testing",
  },
  "Borrowings - Ind AS": {
    "Dashboard & Agreement Summary": "dashboard-agreement-summary",
    "Repayment testing": "repayment-testing",
    "Covenant testing": "covenant-testing",
    "Drawing Power": "drawing-power",
    "Utilisation Testing": "utilisation-testing",
    "Quarterly Stock Statement": "quarterly-stock-statement",
    "Classification testing": "classification-testing",
  },
  "Cash & Cash Equivalents": {
    "Bank Accounts": "bank-accounts",
  },
  "Investments": {
    "Investments Procedures": "investments-procedures",
  },
  "Other Income": {
    "Net Investment in Leases": "net-investment",
    "Other Procedures": "other-procedures",
  },
  "Revenue from Operations": {
    "Ship and Bill - IPE Testing": "ship-bill-ipe-testing",
    "Cost Plus - Computation": "cost-plus-computation",
  },
  "Other Liabilities": {
    "Advance from customer - Completeness Check": "adv-cust-completeness",
    "Advance from customer - Ageing Analysis": "adv-cust-ageing",
    "Capex Vendors - Completeness Check": "capex-vendors-completeness",
    "Capex Vendors - Ageing Analysis": "capex-vendors-ageing",
  },
  "Other Financial Liabilities": {
    "Capex Vendors - Completeness Check": "capex-vendors-completeness",
  },
  "Other Receivables": {
    "Advance to Suppliers - Completeness Check": "adv-suppliers-completeness",
    "Advance to Suppliers - Ageing Analysis": "adv-suppliers-ageing",
    "Capital Advances - Completeness Check": "capital-advances-completeness",
    "Capital Advances - Ageing Analysis": "capital-advances-ageing",
  },
  "Provisions & Contingent Liabilities": {
    "Other Provisions": "other-provisions",
  },
  "Going Concern": {
    "Going Concern Procedures": "going-concern",
    "Assessment": "going-concern",
  },
  "Litigations & Claims": {
    "Litigations & Claims Procedures": "litigations-claims",
    "Assessment": "assessment",
  },
  "Opening Balance Testing": {
    "Inquiry with Predecessor Auditor": "inquiry-predecessor",
    "SA 510 Assessment": "sa510-assessment",
  },
  "Related Party Disclosure": {
    "Compliance": "compliance",
    "Arms-Length Assessment": "arms-length",
  },
  "Management Use of Expert": {
    "Assessment": "assessment",
  },
  "Use of Auditor's Specialist": {
    "Use of Auditor's Specialist Procedures": "use-of-auditors-specialist",
  },
  "Compliance with Laws and Regulations": {
    "Compliance Procedures": "compliance",
  },
  "Review of Estimates for Management Bias": {
    "Review of Estimates Procedures": "review-of-estimates",
  },
  "Internal Audit": {
    "Internal Audit Procedures": "internal-audit",
  },
  "Financial Closing & Reporting Process": {
    "FCRP Procedures": "substantive",
  },
  "Management Override of Controls": {
    "MOC Procedures": "substantive",
  },
  "Biological Assets other than Bearer Plants": {
    "IPE Testing": "ipe-testing",
    "Valuation Testing": "valuation-testing",
    "Physical Verification": "physical-verification",
    "Disclosure Testing": "disclosure-testing",
  },
};

/**
 * Get the route ID for a standard procedure
 * @param workspaceName - The name of the workspace
 * @param procedureName - The name of the procedure
 * @returns The route ID (e.g., "payroll-substantive-ipe_testing" or "ppe-ipe-testing") or empty string if not found
 */
export function getProcedureRouteId(
  workspaceName: string,
  procedureName: string
): string {
  const prefix = getSubstantivePrefixFromWorkspace(workspaceName);
  if (!prefix) {
    return "";
  }

  // Get the procedure ID from the mapping
  const procedureId = PROCEDURE_NAME_TO_ID[workspaceName]?.[procedureName];
  
  // Special case: Going Concern Assessment procedure routes to GoingConcernSubstantiveQuestions
  if (workspaceName === "Going Concern") {
    // If this is the Assessment or Going Concern Procedures, route to the questions component
    if (procedureId === "going-concern" || procedureName === "Assessment" || procedureName === "Going Concern Procedures") {
      return `${prefix}-going-concern`; // Returns "golib-substantive-going-concern"
    }
  }
  
  // Special case: Litigations & Claims procedures
  if (workspaceName === "Litigations & Claims") {
    // Trim procedure name to handle any whitespace issues
    const trimmedProcedureName = procedureName.trim();
    // Assessment procedure routes to assessment component
    if (trimmedProcedureName === "Assessment" || procedureId === "assessment") {
      return `${prefix}-assessment`; // Returns "litclm-substantive-assessment"
    }
    // Litigations & Claims Procedures routes to the questions component
    if (trimmedProcedureName === "Litigations & Claims Procedures" || procedureId === "litigations-claims") {
      return `${prefix}-litigations-claims`; // Returns "litclm-substantive-litigations-claims"
    }
  }
  
  // Special case: Management Use of Expert procedures
  if (workspaceName === "Management Use of Expert") {
    // Trim procedure name to handle any whitespace issues
    const trimmedProcedureName = procedureName.trim();
    // Assessment procedure routes to assessment component
    // Remove "substantive" from prefix to match route registration
    if (trimmedProcedureName === "Assessment" || procedureId === "assessment") {
      const basePrefix = prefix.replace("-substantive", "");
      return `${basePrefix}-assessment`; // Returns "mgtexp-assessment"
    }
  }
  
  if (!procedureId) {
    // Fallback: generate from procedure name
    const fallbackId = procedureName.toLowerCase().replace(/\s+/g, "-");
    // Check if this workspace includes "substantive" in routes
    if (WORKSPACES_WITH_SUBSTANTIVE_IN_ROUTE.has(workspaceName)) {
      return `${prefix}-${fallbackId}`;
    } else {
      // Remove "substantive" from prefix if present (e.g., "ppe-substantive" -> "ppe")
      const basePrefix = prefix.replace("-substantive", "");
      return `${basePrefix}-${fallbackId}`;
    }
  }

  // Check if this workspace includes "substantive" in routes
  if (WORKSPACES_WITH_SUBSTANTIVE_IN_ROUTE.has(workspaceName)) {
    return `${prefix}-${procedureId}`;
  } else {
    // Remove "substantive" from prefix if present (e.g., "ppe-substantive" -> "ppe")
    const basePrefix = prefix.replace("-substantive", "");
    return `${basePrefix}-${procedureId}`;
  }
}

/**
 * Get procedure ID from procedure name for a workspace
 * @param workspaceName - The name of the workspace
 * @param procedureName - The name of the procedure
 * @returns The procedure ID or undefined if not found
 */
export function getProcedureId(
  workspaceName: string,
  procedureName: string
): string | undefined {
  return PROCEDURE_NAME_TO_ID[workspaceName]?.[procedureName];
}

/**
 * Get workspace name from a procedure route ID
 * This is a reverse lookup to determine which workspace a procedure route belongs to
 * @param routeId - The procedure route ID (e.g., "inv-nrv-assessment", "payroll-substantive-ipe_testing")
 * @returns The workspace name or null if not found
 */
export function getWorkspaceFromProcedureRoute(routeId: string): string | null {
  if (!routeId) return null;

  // Check each workspace
  for (const [workspaceName, procedureMap] of Object.entries(PROCEDURE_NAME_TO_ID)) {
    const prefix = WORKSPACE_TO_SUBSTANTIVE_PREFIX[workspaceName];
    if (!prefix) continue;

    // Check if route matches with "substantive" in it
    if (WORKSPACES_WITH_SUBSTANTIVE_IN_ROUTE.has(workspaceName)) {
      // Format: <prefix>-<procedure-id>
      for (const [procedureName, procedureId] of Object.entries(procedureMap)) {
        if (routeId === `${prefix}-${procedureId}`) {
          return workspaceName;
        }
      }
    } else {
      // Format: <base-prefix>-<procedure-id> (without "substantive")
      const basePrefix = prefix.replace("-substantive", "");
      for (const [procedureName, procedureId] of Object.entries(procedureMap)) {
        if (routeId === `${basePrefix}-${procedureId}`) {
          return workspaceName;
        }
      }
    }
  }

  return null;
}

